import React, { useState } from 'react';
import { X, Users, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import salesTeamService from '../services/salesTeamService';

const CustomerBreakdownModal = ({ isOpen, onClose, customerType, customers, memberName }) => {
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  if (!isOpen) return null;

  const typeConfig = {
    ma: { label: 'ลูกค้ารายใหญ่ (MA)', color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-200', badgeBg: 'bg-purple-100' },
    normal: { label: 'ลูกค้าปกติ (Normal)', color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-200', badgeBg: 'bg-blue-100' },
    new: { label: 'ลูกค้าใหม่ (New)', color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-200', badgeBg: 'bg-green-100' }
  };

  const config = typeConfig[customerType] || typeConfig.normal;
  const customerList = customers || [];

  const totalNetAmount = customerList.reduce((sum, c) => sum + (c.net_amount || 0), 0);
  const totalInvoices = customerList.reduce((sum, c) => sum + (c.invoice_count || 0), 0);

  const toggleExpand = (index) => {
    setExpandedCustomer(expandedCustomer === index ? null : index);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className={`${config.bgColor} ${config.borderColor} border-b rounded-t-xl p-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${config.badgeBg} rounded-full flex items-center justify-center`}>
              <Users className={`w-5 h-5 ${config.textColor}`} />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${config.textColor}`}>{config.label}</h2>
              {memberName && <p className="text-sm font-medium text-gray-700">{memberName}</p>}
              <p className="text-sm text-gray-600">
                {customerList.length} ราย | {salesTeamService.formatThaiInteger(totalInvoices)} บิล | รวม {salesTeamService.formatThaiNumber(totalNetAmount)} บาท
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-50 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {customerList.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>ไม่มีข้อมูลลูกค้าประเภทนี้</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customerList.map((customer, index) => (
                <div
                  key={index}
                  className={`border ${config.borderColor} rounded-lg overflow-hidden`}
                >
                  {/* Customer Row */}
                  <div
                    className={`flex items-center justify-between p-3 cursor-pointer hover:${config.bgColor} transition-colors`}
                    onClick={() => toggleExpand(index)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-xs font-medium ${config.textColor} ${config.badgeBg} px-2 py-0.5 rounded-full`}>
                        #{index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 text-sm">{customer.customer_code}</p>
                        <p className="text-xs text-gray-500">{customer.invoice_count} บิล</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className={`font-bold text-sm ${config.textColor}`}>
                          {salesTeamService.formatThaiNumber(customer.net_amount)}
                        </p>
                        {customer.credit_note_amount > 0 && (
                          <p className="text-xs text-red-500">
                            CN: -{salesTeamService.formatThaiNumber(customer.credit_note_amount)}
                          </p>
                        )}
                      </div>
                      {customer.invoice_numbers && customer.invoice_numbers.length > 0 && (
                        expandedCustomer === index
                          ? <ChevronUp className="w-4 h-4 text-gray-400" />
                          : <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Invoice Details (Expanded) */}
                  {expandedCustomer === index && customer.invoice_numbers && customer.invoice_numbers.length > 0 && (
                    <div className={`${config.bgColor} border-t ${config.borderColor} px-4 py-3`}>
                      <div className="flex items-center gap-1 mb-2">
                        <FileText className={`w-3 h-3 ${config.textColor}`} />
                        <p className={`text-xs font-medium ${config.textColor}`}>เลขที่ใบแจ้งหนี้</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {customer.invoice_numbers.map((inv, i) => (
                          <span
                            key={i}
                            className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700 font-mono"
                          >
                            {inv.trim()}
                          </span>
                        ))}
                      </div>
                      {/* Gross vs Net detail */}
                      {customer.gross_amount !== customer.net_amount && (
                        <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                          <p>ยอดรวม: {salesTeamService.formatThaiNumber(customer.gross_amount)} บาท</p>
                          <p className="text-red-500">CN: -{salesTeamService.formatThaiNumber(customer.credit_note_amount)} บาท</p>
                          <p className={`font-medium ${config.textColor}`}>สุทธิ: {salesTeamService.formatThaiNumber(customer.net_amount)} บาท</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerBreakdownModal;
